import React from "react";
import { useTranslation } from "react-i18next";

const GROUPS = [
  {
    id: "group1",
    icon: "fas fa-child",
    nameKey: "taolu_age_groups.group1.name",
    rangeKey: "taolu_age_groups.group1.ageRange",
  },
  {
    id: "group2",
    icon: "fas fa-user-graduate",
    nameKey: "taolu_age_groups.group2.name",
    rangeKey: "taolu_age_groups.group2.ageRange",
  },
  {
    id: "group3",
    icon: "fas fa-user",
    nameKey: "taolu_age_groups.group3.name",
    rangeKey: "taolu_age_groups.group3.ageRange",
  },
  {
    id: "group4",
    icon: "fas fa-user-tie",
    nameKey: "taolu_age_groups.group4.name",
    rangeKey: "taolu_age_groups.group4.ageRange",
  },
  {
    id: "group5",
    icon: "fas fa-user-clock",
    nameKey: "taolu_age_groups.group5.name",
    rangeKey: "taolu_age_groups.group5.ageRange",
  },
];

function AgeGroupsSection() {
  const { t } = useTranslation();

  return (
    <section
      className="relative z-10 bg-slate-50/50 pb-14 pt-12 transition-colors dark:bg-slate-900 md:pb-16 md:pt-14"
      id="age-groups-section"
      aria-labelledby="taolu-age-groups-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mb-10 text-center md:mb-12">
          <h2
            id="taolu-age-groups-heading"
            className="relative mb-5 inline-block text-4xl font-semibold tracking-tight text-primary dark:text-primary-light md:text-5xl"
          >
            {t("taolu_age_groups.title")}
            <span
              className="absolute -bottom-2.5 left-1/2 h-0.5 w-16 -translate-x-1/2 rounded-full bg-primary dark:bg-primary-light"
              aria-hidden
            />
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-slate-600 dark:text-slate-400">
            {t("taolu_age_groups.text")}
          </p>
        </header>

        <ol
          className="m-0 mb-10 grid list-none grid-cols-1 gap-5 p-0 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 md:gap-6"
          aria-label={t("taolu_age_groups.title")}
        >
          {GROUPS.map((group, index) => {
            const n = index + 1;
            const name = t(group.nameKey);
            const ageRange = t(group.rangeKey);
            const headingId = `age-group-title-${group.id}`;

            return (
              <li key={group.id} className="min-h-0">
                <article
                  id={`age-group-${group.id}`}
                  className="group relative h-full w-full overflow-hidden rounded-2xl border border-slate-200 border-l-4 border-l-slate-300 bg-white shadow-sm transition-all dark:border-slate-700 dark:border-l-slate-600 dark:bg-slate-800 motion-safe:hover:shadow-sm"
                  aria-labelledby={headingId}
                >
                  <span
                    className="pointer-events-none absolute right-3 top-2 select-none text-4xl font-black tabular-nums leading-none text-primary/[0.09] dark:text-primary-light/[0.11] sm:right-4 sm:top-2.5 sm:text-5xl md:text-6xl"
                    aria-hidden
                  >
                    {n}
                  </span>
                  <div className="relative z-[1] px-4 pb-4 pt-4 pr-[2.85rem] sm:px-5 sm:pb-5 sm:pt-5 sm:pr-16 md:pr-[4.25rem]">
                    <div className="flex flex-col gap-3">
                      <div
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 transition-transform duration-300 dark:bg-primary-light/15 sm:h-12 sm:w-12 md:h-14 md:w-16 motion-safe:group-hover:scale-105"
                        aria-hidden
                      >
                        <i
                          className={`${group.icon} text-lg text-primary dark:text-primary-light md:text-xl lg:text-2xl`}
                        />
                      </div>
                      <div className="min-w-0 text-left">
                        <h3
                          id={headingId}
                          className="text-pretty text-base font-semibold leading-snug tracking-tight text-slate-900 dark:text-slate-100"
                        >
                          {name}
                        </h3>
                        <p className="mt-1 text-sm leading-snug text-slate-500 dark:text-slate-400">
                          <span className="sr-only">
                            {t("taolu_age_groups.age_range_sr")}:{" "}
                          </span>
                          {ageRange}
                        </p>
                      </div>
                    </div>
                  </div>
                </article>
              </li>
            );
          })}
        </ol>

        <aside
          className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-slate-200 border-l-4 border-l-slate-300 bg-white shadow-sm transition-all dark:border-slate-700 dark:border-l-slate-600 dark:bg-slate-800 motion-safe:hover:shadow-sm"
          aria-label={t("taolu_age_groups.note_label")}
        >
          <div className="flex items-start gap-4 px-5 py-5 sm:gap-5 sm:px-6 sm:py-6">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 dark:bg-primary-light/15 sm:h-12 sm:w-12"
              aria-hidden
            >
              <i className="fas fa-info-circle text-lg text-primary dark:text-primary-light sm:text-xl" />
            </div>
            <p className="min-w-0 flex-1 text-pretty text-sm leading-relaxed text-slate-600 dark:text-slate-400 sm:text-base">
              {t("taolu_age_groups.note")}
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}

export default AgeGroupsSection;
